/* eslint-disable no-console */
/* eslint-disable no-useless-escape */
import { apolloClient } from "@/plugins/apollo";
import { gql } from "@apollo/client/core";
import dayjs from "@/plugins/dayjs";
import { DateFormats } from "@/utils/formats";

import { FinnishBusinessIds } from "finnish-business-ids";
import { FinnishSSN } from "finnish-ssn";

import * as ibantools from "ibantools";

import { checkVAT, countries as jsvatCountries } from "jsvat-next";
import { trim } from "lodash-es";

export const emailValidationRegex =
  /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/g;

function checkValuePresence(v) {
  if (v == null || v == undefined) return false;
  if ((v ?? true) === true || String(v)?.length <= 0) return false;
  return true;
}

function getRegistryCountryAlpha(registryCountryId) {
  return apolloClient.readFragment({
    id: "Country:" + registryCountryId,
    fragment: gql`
      fragment MyTodo on Country {
        id
        alpha2
      }
    `
  })?.alpha2;
}

export function dateValidation(date) {
  if (!checkValuePresence(date)) return true;
  return dayjs(date, DateFormats, true).isValid();
}

export function emailValidation(value) {
  if (!checkValuePresence(value)) return true;
  return value.match(emailValidationRegex) ? true : false;
}

export function priceDistributionValidation(value) {
  if (!checkValuePresence(value)) return true;
  var validated = true;

  var distribution = value.split(",").filter(item => item.length > 0);
  if (distribution.length == 0) return true;

  for (var x = 0; distribution.length > x; x++) {
    if (
      Number.isNaN(parseFloat(distribution[x])) ||
      distribution[x].match(/(?!\.)([^\d])/g)
    )
      validated = false;
  }
  return validated;
}

export function ibanValidation(value) {
  if (!checkValuePresence(value)) return true;
  value = String(value).split(" ").join("");
  return ibantools.isValidIBAN(value);
}

export function bankAccountValidation(value) {
  if (!checkValuePresence(value)) return true;
  value = String(value).split(" ").join("");
  const country = process.env.VUE_APP_COUNTRY;
  /*getRegistryCountryAlpha(siblings?.registryCountryId) ??*/

  return ibantools.isValidBBAN(value, country) || ibantools.isValidIBAN(value);
}

export function personRegistryCodeValidation(value, siblings) {
  if (!checkValuePresence(value)) return true;
  let registryCountry = process.env.VUE_APP_COUNTRY;

  if (siblings?.registryCountryId) {
    registryCountry = getRegistryCountryAlpha(siblings.registryCountryId);
  }

  // only validate if its same as federation's country
  if (registryCountry != process.env.VUE_APP_COUNTRY) return true;

  switch (registryCountry) {
    case "FI": {
      try {
        let id = FinnishSSN.validate(value);
        if (id) return true;
      } catch (err) {
        //
      }
      return false;
    }

    case "BE": {
      // taken from https://docs.trellix.com/bundle/data-loss-prevention-11.10.x-classification-definitions-reference-guide/page/GUID-4EB8CF82-8D58-4681-BA6F-B97A979CB6AF.html
      // modified to allow dash and dots
      return /\b[0-9]{2}.?(?:0[1-9]|1[0-2]).?(?:0[1-9]|[12][0-9]|3[01])[ -.]?[0-9]{3}[ .-]?(?:0[1-9]|[1-8][0-9]|9[0-7])\b/g.test(
        value
      );
    }
    default:
      return true;
  }
}

export function businessRegistryCodeValidation(value, siblings) {
  if (!checkValuePresence(value)) return true;
  let registryCountry = process.env.VUE_APP_COUNTRY;

  if (siblings?.registryCountryId) {
    registryCountry = getRegistryCountryAlpha(siblings.registryCountryId);
  }

  // only validate if its same as federation's country
  if (registryCountry != process.env.VUE_APP_COUNTRY) return true;

  switch (registryCountry) {
    case "FI": {
      try {
        let id = FinnishBusinessIds.isValidBusinessId(value);
        if (id) return true;
      } catch (err) {
        //
      }
      return false;
    }

    case "BE": {
      return /^(BE){0,1}[0|1]{0,1}[0-9]{9}$/g.test(
        value.replaceAll(/[-. ]/g, "")
      );
    }
    default:
      return true;
  }
}

export function swiftBicValidation(value) {
  if (!checkValuePresence(value)) return true;
  // 4 letters, country code, 2 letters or digits, optional 3 letters or digits
  const regex =
    /(^[A-Z]{4}[A-Z]{2}[0-9,A-Z]{2}$)|(^[A-Z]{4}[A-Z]{2}[0-9,A-Z]{2}[0-9A-Z]{3}$)/g;
  return regex.test(value) === true;
}

export function urlValidation(value) {
  if (!checkValuePresence(value)) return true;

  const expression =
    /[-a-zA-Z0-9@:%_\+.~#?&//=]{2,256}\.[a-z]{2,4}\b(\/[-a-zA-Z0-9@:%_\+.~#?&//=]*)?/gi;
  // Check if domain is correct
  if (!new RegExp(expression).test(value)) {
    return false;
  }
  // Check if protocol included
  try {
    new URL(value);
    return true;
  } catch (err) {
    //console.log(err);
    return false;
  }
}

export function customRegexValidation(value, pattern) {
  if (!checkValuePresence(value)) return true;
  return new RegExp(pattern).test(value);
}

export async function vatCodeValidation(value, siblings) {
  if (!checkValuePresence(value)) return true;

  if (siblings.vatCodeViesCheckPassed === undefined) {
    siblings.vatCodeViesCheckPassed = null;
  }

  let passed = true;
  value = value.trim().replaceAll(/[\s.]/g, "");
  //console.log(value);
  //console.log(siblings, vm);
  const vatResult = checkVAT(value, jsvatCountries);

  const { countries } = apolloClient.readQuery({
    query: gql`
      query countriesVatCodeVaildation {
        countries {
          id
          alpha2
          inEuVat
        }
      }
    `
  });

  let country = countries.find(x =>
    vatResult.isValid
      ? x.alpha2 == vatResult.country?.isoCode?.short
      : x.id == siblings.registryCountryId
  );

  if (!country?.inEuVat) {
    //console.warn("Not in EU VAT", country, value, vatResult.country);
    siblings.vatCodeViesCheckPassed = null;
    return true;
  }

  // Following code is for EU VAT
  if (vatResult.isValid) {
    try {
      const { data } = await apolloClient.query({
        query: gql`
          query valvat($input: String!) {
            valvat(input: $input)
          }
        `,
        variables: {
          input: value
        },
        fetchPolicy: "cache-first"
      });

      if (data.valvat === true) {
        siblings.vatCodeViesCheckPassed = true;
      } else if (data.valvat === false) {
        passed = false; // strictly expect false value to consider it as failure
        siblings.vatCodeViesCheckPassed = false;
      }
    } catch (error) {
      //passed = false;
      siblings.vatCodeViesCheckPassed = false;
      console.error(error);
    }
  } else {
    console.warn("vat is not valid");
    passed = false;
  }

  return passed;
}

/**
 * Find if there is another field with exact same field
 * @param {*} value field's value
 * @param {object} options.siblings form object
 * @param {string[]} options.fieldNames field names where uniqueness should be conducted
 * @returns true if unique. false if there's another field with same value
 */
export function uniquenessValidation(value, options) {
  if (!value) return true;
  if (options.fieldNames.length == 0) return true;

  const trimeed = trim(value);
  const vals = Object.entries(options.siblings).filter(([k]) =>
    options.fieldNames.includes(k)
  );
  return vals.filter(([, v]) => v == trimeed).length <= 1;
}
